import { AnimatePresence, motion, Reorder } from "motion/react"
import { ComponentType, type CSSProperties } from "react"

interface ListProps<T extends { id: string }> {
  style?: CSSProperties
  items: T[]
  component: ComponentType<{
    item: T
    onClick?: (id: string) => void
  }>
  gap?: number
  layout?: "vertical" | "grid"
  separator?: boolean
  noScroll?: boolean
  disableLayoutAnimation?: boolean
  onClick?: (id: string) => void
}

const List = <T extends { id: string }>({
  items,
  noScroll = false,
  component: Component,
  layout = "grid",
  gap = 30,
  separator = false,
  disableLayoutAnimation = false,
  style,
  onClick,
}: ListProps<T>) => {
  const isVertical = layout === "vertical"

  const containerStyle: CSSProperties = {
    overflowY: noScroll ? "visible" : "auto",
    height: "100%",
    width: "100%",
    paddingBottom: noScroll ? 0 : 100,
    display: "flex",
    flexDirection: isVertical ? "column" : "row",
    flexWrap: isVertical ? "nowrap" : "wrap",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    gap,
    ...style,
  }

  // Add separator styles for vertical layout
  if (isVertical && separator) {
    containerStyle.gap = 0
  }

  return (
    <Reorder.Group
      axis="y"
      onReorder={() => {}} // Keep items in original order
      layoutScroll
      values={items}
      style={containerStyle}
    >
      <AnimatePresence mode="popLayout">
        {items.map((item, index) => {
          const itemStyle: CSSProperties = {}

          // Add separator border for vertical layout
          if (isVertical && separator && index < items.length - 1) {
            itemStyle.borderBottom = "1px solid #d9d9d9"
            itemStyle.paddingBottom = "12px"
            itemStyle.marginBottom = "12px"
          }

          const itemMotion = {
            initial: { opacity: 0, x: 4 },
            animate: { opacity: 1, x: 0 },
            exit: { opacity: 0, x: -4 },
            transition: { duration: 0.5, ease: "easeInOut" as const },
            style: itemStyle,
          }
          const row = (
            <Component item={item} onClick={() => onClick?.(item.id)} />
          )

          // Reorder.Item types layout as `true | "position"` and defaults an
          // omitted value to true, so it cannot turn the animation off.
          // motion.li accepts false, which is what the flag is for.
          if (disableLayoutAnimation) {
            return (
              <motion.li key={item.id} layout={false} {...itemMotion}>
                {row}
              </motion.li>
            )
          }

          return (
            <Reorder.Item key={item.id} value={item} {...itemMotion}>
              {row}
            </Reorder.Item>
          )
        })}
      </AnimatePresence>
    </Reorder.Group>
  )
}

export default List
